/**
 * Print Materials Card
 * Download decals for print-at-home referral materials
 */

import { useState } from 'react';
import { Printer, Download, Loader2, FileImage, FileCode } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { generateDecal, getDecalDimensions } from '../utils/decal-generator';
import { downloadSvgAsPng, downloadSvg, getReferralUrl } from '../utils/helpers';
import { DECAL_SIZES, DECAL_TYPES } from '../utils/constants';
import type { DecalType, DecalSize } from '../types';

interface PrintMaterialsCardProps {
  referralCode: string;
}

interface DecalPreviewProps {
  type: DecalType;
  referralCode: string;
  referralUrl: string;
  onDownload: (type: DecalType, size: DecalSize) => Promise<void>;
  isDownloading: boolean;
  downloadingKey: string | null;
}

function DecalPreview({
  type,
  referralCode,
  referralUrl,
  onDownload,
  isDownloading,
  downloadingKey,
}: DecalPreviewProps) {
  const config = DECAL_TYPES[type];
  const sizes: DecalSize[] = ['small', 'medium', 'large'];

  // Generate preview at small size
  const preview = generateDecal(referralUrl, referralCode, type, 'small');

  return (
    <div className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors">
      {/* Preview */}
      <div className="aspect-square bg-white rounded-lg p-3 mb-3 border border-gray-200 flex items-center justify-center overflow-hidden">
        <div
          className="w-full h-full"
          dangerouslySetInnerHTML={{ __html: preview.svg }}
          style={{ maxWidth: '100%', maxHeight: '100%' }}
        />
      </div>

      {/* Title */}
      <h4 className="font-medium text-gray-900 text-sm">{config.label}</h4>
      <p className="text-xs text-gray-500 mb-3">{config.description}</p>

      {/* Size buttons */}
      <div className="flex flex-wrap gap-2">
        {sizes.map((size) => {
          const sizeConfig = DECAL_SIZES[size];
          const isLoading = isDownloading && downloadingKey === `${type}-${size}`;

          return (
            <button
              key={size}
              onClick={() => onDownload(type, size)}
              disabled={isDownloading || !referralCode}
              className="flex-1 min-w-[60px] px-2 py-1.5 text-xs font-medium bg-white border border-gray-200 rounded-lg hover:bg-primary-50 hover:border-primary-200 hover:text-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <Loader2 className="w-3 h-3 animate-spin mx-auto" />
              ) : (
                sizeConfig.label
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function PrintMaterialsCard({ referralCode }: PrintMaterialsCardProps) {
  const { t } = useLanguage();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);

  const referralUrl = getReferralUrl(referralCode);
  const decalTypes: DecalType[] = ['circular', 'rectangular', 'square'];

  const handleDownload = async (type: DecalType, size: DecalSize) => {
    if (!referralCode) return;

    setIsDownloading(true);
    setDownloadingKey(`${type}-${size}`);

    try {
      const decal = generateDecal(referralUrl, referralCode, type, size);
      const dimensions = getDecalDimensions(type, size);

      if (size === 'vector') {
        downloadSvg(decal.svg, decal.filename);
      } else {
        await downloadSvgAsPng(decal.svg, decal.filename, dimensions.width, dimensions.height);
      }
    } catch (error) {
      console.error('Failed to download decal:', error);
    } finally {
      setIsDownloading(false);
      setDownloadingKey(null);
    }
  };

  const handleDownloadVector = async (type: DecalType) => {
    await handleDownload(type, 'vector');
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-200">
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Printer className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t('referrals.printMaterials.title', 'Print Materials')}
            </h2>
            <p className="text-sm text-gray-500">
              {t('referrals.printMaterials.subtitle', 'Download decals to share at your local IKEA!')}
            </p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Decal Previews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {decalTypes.map((type) => (
            <DecalPreview
              key={type}
              type={type}
              referralCode={referralCode}
              referralUrl={referralUrl}
              onDownload={handleDownload}
              isDownloading={isDownloading}
              downloadingKey={downloadingKey}
            />
          ))}
        </div>

        {/* Vector Downloads Section */}
        <div className="border-t border-gray-100 pt-4">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
            <FileCode className="w-4 h-4" />
            <span className="font-medium">
              {t('referrals.printMaterials.vectorTitle', 'Vector Downloads (SVG)')}
            </span>
            <span className="text-gray-400">-</span>
            <span className="text-xs text-gray-500">
              {t('referrals.printMaterials.vectorHint', 'For custom sizes or professional printing')}
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            {decalTypes.map((type) => {
              const config = DECAL_TYPES[type];
              const isLoading = isDownloading && downloadingKey === `${type}-vector`;

              return (
                <button
                  key={type}
                  onClick={() => handleDownloadVector(type)}
                  disabled={isDownloading || !referralCode}
                  className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-primary-100 hover:text-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span>{config.label} SVG</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Info note */}
        <div className="mt-4 flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
          <FileImage className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
          <p className="text-xs text-blue-700">
            {t(
              'referrals.printMaterials.printTip',
              'Decals are designed for 300 DPI printing. For best results, print at actual size on sticker paper or have professionally printed.'
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
